import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useState } from "react";
import { toast } from "react-toastify";
import { authorizeStripeCard, getStripeCardDetails } from "../api/apiFunctions";

function SaveCardForm({ onCardSaved }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      // generate payment method
      const { paymentMethod: stripePaymentMethod, error: stripeError } =
        await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardNumberElement),
        });

      if (stripeError) {
        toast.error(stripeError.message);
        setLoading(false);
        return;
      }

      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");

      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const payload = {
        paymentMethod: stripePaymentMethod.id,
        displayBrand: stripePaymentMethod.card?.brand,
        last4: stripePaymentMethod.card?.last4,
        expMonth: String(stripePaymentMethod.card?.exp_month || ""),
        expYear: String(stripePaymentMethod.card?.exp_year || ""),
      };

      // save card
      const authorizeResponse = await authorizeStripeCard(payload, token);

      // Non 3DS – card saved directly
      if (authorizeResponse.success && !authorizeResponse.requiresAction) {
        try {
          const cardRes = await getStripeCardDetails(token);
          const hasCard = Boolean(cardRes?.cardDetails);

          if (!hasCard) {
            toast.error(
              "Card was authorized but no active card was found. Please try again or use a different card.",
            );
            return;
          }
        } catch (err) {
          console.error("Error while getting card details", err);
          toast.error(
            err.message ||
              "Card was authorized but we could not confirm it as active. Please try again.",
          );
          return;
        }

        toast.success("Card saved successfully.");
        if (onCardSaved) {
          onCardSaved({
            type: "non_3ds",
            paymentMethod: stripePaymentMethod,
            customerId: authorizeResponse.customerId,
          });
        }
      }
      // 3DS required – confirm setup on client
      else if (
        authorizeResponse.requiresAction &&
        authorizeResponse.clientSecret
      ) {
        const result = await stripe.confirmCardSetup(
          authorizeResponse.clientSecret,
        );

        if (result.error) {
          throw new Error(result.error.message);
        }

        if (result.setupIntent.status !== "succeeded") {
          throw new Error(
            `Card verification failed with status: ${result.setupIntent.status}`,
          );
        }

        try {
          const cardRes = await getStripeCardDetails(token);
          const hasCard = Boolean(cardRes?.cardDetails);

          if (!hasCard) {
            toast.error(
              "Card was verified but no active card was found. Please try again or use a different card.",
            );
            return;
          }
        } catch (err) {
          console.error("Card details check failed:", err);
          toast.error(
            err.message ||
              "Card was verified but we could not confirm it as active. Please try again.",
          );
          return;
        }

        toast.success("Card verified and saved successfully.");
        if (onCardSaved) {
          onCardSaved({
            type: "3ds",
            paymentMethod: stripePaymentMethod,
            customerId: authorizeResponse.customerId,
          });
        }
      } else {
        throw new Error(
          authorizeResponse.message || "Failed to authorize card.",
        );
      }
    } catch (err) {
      console.log("Save card error:", err);
      toast.error(err?.message ?? "Failed to save card.");
    } finally {
      setLoading(false);
    }
  };

  const elementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#1f2937",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        fontWeight: "500",
        "::placeholder": {
          color: "#9ca3af",
        },
        iconColor: "#4962d8",
      },
      invalid: {
        color: "#ef4444",
        iconColor: "#ef4444",
      },
    },
  };

  const inputClassName =
    "mt-1 block w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl shadow-sm focus:border-[#4962d8] focus:ring-2 focus:ring-[#4962d8]/20 transition-all";

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-xl border border-[#e5e7eb]">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <div className="bg-gradient-to-br from-[#445eed] to-[#2d348f] rounded-full p-4 mb-4 shadow-lg">
            <svg
              width="48"
              height="48"
              fill="none"
              viewBox="0 0 24 24"
              stroke="white"
              strokeWidth="2"
            >
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="M2 10h20" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="text-3xl font-extrabold text-[#2d348f] mb-2 text-center">
            Save Your Card
          </h2>
          <p className="text-[#837f90] text-center text-base">
            Securely save your card to continue with subscription
          </p>
        </div>

        {/* Card Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Card Number */}
          <div className="relative">
            <label className="block text-xs font-semibold text-[#837f90] mb-2">
              Card Number
            </label>
            <div className={inputClassName}>
              <CardNumberElement options={elementOptions} />
            </div>
          </div>

          {/* Expiry and CVC Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-xs font-semibold text-[#837f90] mb-2">
                Expiry Date
              </label>
              <div className={inputClassName}>
                <CardExpiryElement options={elementOptions} />
              </div>
            </div>

            <div className="relative">
              <label className="block text-xs font-semibold text-[#837f90] mb-2">
                CVC
              </label>
              <div className={inputClassName}>
                <CardCvcElement options={elementOptions} />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!stripe || loading}
            className="w-full py-4 font-bold text-white text-base bg-gradient-to-r from-[#445eed] to-[#2d348f] hover:from-[#3916ab] hover:to-[#4962d8] transition-all rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving Card...
              </span>
            ) : (
              "Save Card"
            )}
          </button>

          {/* Security Note */}
          <div className="flex items-center justify-center text-xs text-gray-500 pt-2">
            <svg
              className="w-4 h-4 mr-1.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            Your payment information is secure and encrypted
          </div>
        </form>
      </div>
    </div>
  );
}

export default SaveCardForm;
